import { AnimatePresence, motion } from 'framer-motion'
import {
  Building,
  Calendar,
  Copy,
  Database,
  Download,
  ExternalLink,
  FileText,
  Info,
  Search,
  TrendingUp,
  User
} from 'lucide-react'
import { useContext, useState } from 'react'
import logger from '../lib/logger'
import { ThemeContext } from '../contexts/ThemeContext'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Checkbox } from './ui/checkbox'
import { Input } from './ui/input'
import { Progress } from './ui/progress'

const PatentSearch = () => {
  const { isDarkMode: darkMode } = useContext(ThemeContext)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchMode, setSearchMode] = useState('keyword') // 只保留關鍵字檢索
  const [isSearching, setIsSearching] = useState(false)
  const [searchProgress, setSearchProgress] = useState(0)
  const [searchResults, setSearchResults] = useState([])
  const [selectedPatents, setSelectedPatents] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // 移除模擬資料，現在使用真實API

  // 格式化日期函數
  const formatDate = (dateString) => {
    if (!dateString) return '未知'

    // 處理 YYYYMMDD 格式 (如 20231117)
    if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
      const year = dateString.substring(0, 4)
      const month = dateString.substring(4, 6)
      const day = dateString.substring(6, 8)
      return `${year}-${month}-${day}`
    }

    // 其他格式直接返回
    return dateString
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setIsSearching(true)
    setSearchProgress(0)
    setSearchResults([])
    setCurrentPage(1) // 重置頁碼

    // 搜尋進度更新
    const progressInterval = setInterval(() => {
      setSearchProgress(prev => {
        if (prev >= 80) {
          clearInterval(progressInterval)
          return 80
        }
        return prev + 20
      })
    }, 300)

    try {
      // 關鍵字檢索模式
      const queryParam = searchQuery

      const params = new URLSearchParams({
        query: queryParam,
        limit: '20'
      })

      // 呼叫真實API
      const response = await fetch(`/api/patent/search?${params}`)
      const data = await response.json()

      setSearchProgress(100)
      clearInterval(progressInterval)

      if (response.ok && data.success) {
        // 處理成功回應
        const patents = data.all_results || []

        // 直接使用所有回傳的專利結果
        const filteredPatents = patents

        logger.info(`顯示 ${filteredPatents.length} 個專利結果`)

        setSearchResults(filteredPatents.map((patent, index) => ({
          id: patent.patent_number || `patent-${Date.now()}-${index}`,
          title: patent.title || patent.english_title || '未知標題',
          abstract: patent.abstract || '無摘要',
          database: patent.database || patent.source_database || 'TIPO',
          jurisdiction: patent.jurisdiction === 'UNKNOWN' ? 'TW' : (patent.jurisdiction || 'TW'),
          patent_type: patent.patent_type || '',
          status: patent.legal_status || 'Unknown',
          relevanceScore: patent.relevance_score || 0.5,
          inventors: Array.isArray(patent.inventors) ? patent.inventors : (patent.inventors ? patent.inventors.split(',').map(i => i.trim()) : []),
          applicants: Array.isArray(patent.applicants) ? patent.applicants : (patent.applicants ? patent.applicants.split(',').map(a => a.trim()) : []),
          assignee: patent.assignee || patent.applicants || '未知',
          publicationDate: formatDate(patent.publication_date),
          filingDate: formatDate(patent.filing_date),
          classifications: Array.isArray(patent.classifications) ? patent.classifications : (patent.classifications ? patent.classifications.split(',').map(c => c.trim()) : []),
          citationCount: patent.citation_count || 0,
          familySize: patent.family_size || 1,
          language: patent.language || 'zh',
          patent_url: patent.patent_url || '',  // 專利詳細頁面連結
          search_url: patent.search_url || ''   // 原始資料庫搜尋連結
        })))
      } else {
        // 處理API錯誤
        let errorMessage = '搜尋失敗'

        if (data.error) {
          errorMessage = data.error
        } else if (data.error_message) {
          errorMessage = data.error_message
        } else if (data.results_by_database) {
          // 顯示各資料庫的錯誤狀態
          const errors = Object.entries(data.results_by_database)
            .filter(([_, result]) => !result.success)
            .map(([db, result]) => `${db}: ${result.error}`)

          if (errors.length > 0) {
            errorMessage = `${'資料庫連接失敗'}:\n${errors.join('\n')}`
          }
        }

        logger.error('搜尋失敗:', errorMessage)
        alert(errorMessage)
        setSearchResults([])
      }
    } catch (error) {
      clearInterval(progressInterval)
      setSearchProgress(100)
      logger.error('搜尋請求失敗:', error)
      alert(`${'網絡錯誤'}: ${error.message}`)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleLoadMore = async () => {
    if (!searchQuery.trim() || isLoadingMore) return

    setIsLoadingMore(true)

    try {
      const nextPage = currentPage + 1

      const queryParam = searchQuery

      const params = new URLSearchParams({
        query: queryParam,
        limit: '20',
        offset: ((nextPage - 1) * 20).toString() // 計算偏移量
      })

      logger.info(`載入更多結果，第 ${nextPage} 頁`)

      const response = await fetch(`/api/patent/search?${params}`)
      const data = await response.json()

      if (response.ok && data.success) {
        const newPatents = data.all_results || []
        logger.info(`API回傳 ${newPatents.length} 個新專利結果`)

        // 直接使用所有新的專利結果
        const filteredNewPatents = newPatents

        logger.info(`載入 ${filteredNewPatents.length} 個新專利結果`)

        if (filteredNewPatents.length > 0) {
          const formattedNewPatents = filteredNewPatents.map((patent, index) => ({
            id: patent.patent_number || `patent-${Date.now()}-${searchResults.length + index}`,
            title: patent.title || patent.english_title || '未知標題',
            abstract: patent.abstract || '無摘要',
            database: patent.database || patent.source_database || 'TIPO',
            jurisdiction: patent.jurisdiction === 'UNKNOWN' ? 'TW' : (patent.jurisdiction || 'TW'),
            patent_type: patent.patent_type || '',
            status: patent.legal_status || 'Unknown',
            relevanceScore: patent.relevance_score || 0.5,
            inventors: Array.isArray(patent.inventors) ? patent.inventors : (patent.inventors ? patent.inventors.split(',').map(i => i.trim()) : []),
            applicants: Array.isArray(patent.applicants) ? patent.applicants : (patent.applicants ? patent.applicants.split(',').map(a => a.trim()) : []),
            assignee: patent.assignee || patent.applicants || '未知',
            publicationDate: formatDate(patent.publication_date),
            filingDate: formatDate(patent.filing_date),
            classifications: Array.isArray(patent.classifications) ? patent.classifications : (patent.classifications ? patent.classifications.split(',').map(c => c.trim()) : []),
            citationCount: patent.citation_count || 0,
            familySize: patent.family_size || 1,
            language: patent.language || 'zh',
            patent_url: patent.patent_url || '',
            search_url: patent.search_url || ''
          }))

          // 去重：檢查是否已存在相同專利
          const existingIds = new Set(searchResults.map(p => p.id))
          const uniqueNewPatents = formattedNewPatents.filter(patent => !existingIds.has(patent.id))

          if (uniqueNewPatents.length > 0) {
            setSearchResults(prev => [...prev, ...uniqueNewPatents])
            setCurrentPage(nextPage)
            logger.info(`載入 ${uniqueNewPatents.length} 個新專利（已去重）`)
          } else {
            logger.info('沒有新的結果了（可能存在重複）')
            alert('沒有更多結果')
          }
        } else {
          logger.info('沒有更多結果了')
          alert('響應中沒有結果數據')
        }
      } else {
        logger.error('載入更多結果失敗:', data)
        alert('載入更多結果失敗')
      }
    } catch (error) {
      logger.error('載入更多請求失敗:', error)
      alert(`${'載入更多結果失敗'}: ${error.message}`)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const togglePatentSelection = (patentId) => {
    setSelectedPatents(prev =>
      prev.includes(patentId)
        ? prev.filter(id => id !== patentId)
        : [...prev, patentId]
    )
  }

  const PatentCard = ({ patent }) => {
    const isSelected = selectedPatents.includes(patent.id)

    // 取得資料庫顯示名稱和國家旗幟
    const getJurisdictionInfo = (jurisdiction) => {
      const jurisdictionMap = {
        'US': { country: '🇺🇸', name: '美國', code: 'US' },
        'EP': { country: '🇪🇺', name: '歐洲', code: 'EP' },
        'JP': { country: '🇯🇵', name: '日本', code: 'JP' },
        'CN': { country: '🇨🇳', name: '中國', code: 'CN' },
        'TW': { country: '🇹🇼', name: '台灣', code: 'TW' },
        'KR': { country: '🇰🇷', name: '韓國' || '韓國', code: 'KR' }
      }
      return jurisdictionMap[jurisdiction] || { country: '🌍', name: jurisdiction || '其他' || '其他', code: jurisdiction || 'XX' }
    }

    // 提取中文摘要部分
    const extractChineseAbstract = (abstract) => {
      if (!abstract || abstract === '無摘要' || abstract.trim() === '') {
        return ''
      }

      // 常見的中英文分隔模式 - 更全面的匹配
      const patterns = [
        // 括號開頭的英文模式
        /^(.*?)(?:\s*\([Tt]his\s+|\s*This\s+)/,
        /^(.*?)(?:\s*\([Aa]\s+|\s*[Aa]\s+)/,
        /^(.*?)(?:\s*\([Tt]he\s+|\s*The\s+)/,
        /^(.*?)(?:\s*\([Ii]t\s+|\s*It\s+)/,
        /^(.*?)(?:\s*\([Ss]ystem\s+|\s*System\s+)/,
        /^(.*?)(?:\s*\([Mm]ethod\s+|\s*Method\s+)/,
        /^(.*?)(?:\s*\([Dd]evice\s+|\s*Device\s+)/,
        /^(.*?)(?:\s*\([Aa]pparatus\s+|\s*Apparatus\s+)/,

        // 常見的專利英文片段
        /^(.*?)(?:\s*\[?\s*Problem\s+to\s+be\s+Solved\s*\]?)/i,
        /^(.*?)(?:\s*\[?\s*Technical\s+Field\s*\]?)/i,
        /^(.*?)(?:\s*\[?\s*Background\s+Art\s*\]?)/i,
        /^(.*?)(?:\s*\[?\s*Summary\s*\]?)/i,
        /^(.*?)(?:\s*\[?\s*Disclosure\s*\]?)/i,
        /^(.*?)(?:\s*\[?\s*Solution\s*\]?)/i,
        /^(.*?)(?:\s*\[?\s*Means\s+for\s+Solving\s*\]?)/i,

        // 專利結構標記
        /^(.*?)(?:\s*\(\s*[Tt]echnical\s+[Ff]ield\s*\))/,
        /^(.*?)(?:\s*\(\s*[Bb]ackground\s+[Aa]rt\s*\))/,
        /^(.*?)(?:\s*\(\s*[Ss]ummary\s+of\s+[Ii]nvention\s*\))/,

        // 一般英文句子開頭
        /^(.*?)(?:\s*[Tt]o\s+provide\s+)/,
        /^(.*?)(?:\s*[Tt]o\s+solve\s+)/,
        /^(.*?)(?:\s*[Tt]he\s+present\s+invention\s+)/,
        /^(.*?)(?:\s*[Aa]n\s+object\s+of\s+)/,
        /^(.*?)(?:\s*[Tt]he\s+invention\s+)/,
        /^(.*?)(?:\s*[Aa]ccording\s+to\s+)/,
        /^(.*?)(?:\s*[Ii]n\s+order\s+to\s+)/,
      ]

      // 嘗試各種模式
      for (const pattern of patterns) {
        const match = abstract.match(pattern)
        if (match && match[1] && match[1].trim().length > 0) {
          const chinesePart = match[1].trim()
          // 確保提取的部分包含中文字符且長度合理
          if (/[\u4e00-\u9fff]/.test(chinesePart) && chinesePart.length > 5) {
            // 進一步清理可能殘留的英文片段
            let cleanedPart = chinesePart
              .replace(/\s*\[?\s*[Pp]roblem\s+to\s+be\s+[Ss]olved\s*\]?\s*$/i, '')
              .replace(/\s*\[?\s*[Tt]echnical\s+[Ff]ield\s*\]?\s*$/i, '')
              .replace(/\s*\[?\s*[Bb]ackground\s+[Aa]rt\s*\]?\s*$/i, '')
              .replace(/\s*\[?\s*[Ss]ummary\s*\]?\s*$/i, '')
              .replace(/\s*\[?\s*[Ss]olution\s*\]?\s*$/i, '')
              .replace(/\s*[Tt]o\s+provide\s*$/i, '')
              .replace(/\s*[Tt]o\s+solve\s*$/i, '')
              .trim()

            if (cleanedPart.length > 5 && /[\u4e00-\u9fff]/.test(cleanedPart)) {
              return cleanedPart
            }
          }
        }
      }

      // 如果沒有匹配到模式，檢查是否包含中文，如果有就返回整段
      if (/[\u4e00-\u9fff]/.test(abstract)) {
        // 嘗試按句號分割，只保留包含中文的句子
        const sentences = abstract.split(/[。．]/);
        const chineseSentences = sentences
          .filter(sentence => {
            const trimmed = sentence.trim()
            return /[\u4e00-\u9fff]/.test(trimmed) &&
              !trimmed.match(/^\s*\[?\s*[A-Za-z\s]+\s*\]?\s*$/i) // 排除純英文片段
          })
          .map(sentence => sentence.trim())
          .filter(sentence => sentence.length > 0)

        if (chineseSentences.length > 0) {
          let result = chineseSentences.join('。').trim()
          // 最終清理
          result = result
            .replace(/\s*\[?\s*[Pp]roblem\s+to\s+be\s+[Ss]olved\s*\]?\s*/gi, '')
            .replace(/\s*\[?\s*[Tt]echnical\s+[Ff]ield\s*\]?\s*/gi, '')
            .replace(/\s*\[?\s*[Bb]ackground\s+[Aa]rt\s*\]?\s*/gi, '')
            .replace(/\s*\[?\s*[Ss]ummary\s*\]?\s*/gi, '')
            .replace(/\s*\[?\s*[Ss]olution\s*\]?\s*/gi, '')
            .replace(/\s*[Tt]o\s+provide\s*/gi, '')
            .trim()

          if (result.length > 0) {
            return result
          }
        }

        // 最後嘗試：移除所有英文片段，只保留中文內容
        let cleanedAbstract = abstract
          .replace(/\[?\s*[A-Za-z\s]+\s*\]?/g, ' ') // 移除所有英文片段
          .replace(/\s+/g, ' ') // 合併多個空格
          .trim()

        if (/[\u4e00-\u9fff]/.test(cleanedAbstract) && cleanedAbstract.length > 5) {
          return cleanedAbstract
        }
      }

      // 如果完全沒有中文，返回空字符串
      return ''
    }

    const jurisdictionInfo = getJurisdictionInfo(patent.jurisdiction)

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`border rounded-lg p-6 hover:shadow-md transition-all duration-200 ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          }`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => togglePatentSelection(patent.id)}
              className="mt-1"
            />
            <Badge variant="outline" className="text-xs flex items-center space-x-1 px-2 py-1">
              <span>{jurisdictionInfo.country}</span>
              <span>{jurisdictionInfo.name}</span>
            </Badge>
            <Badge variant="outline" className="text-xs px-2 py-1">
              {patent.database}
            </Badge>
            <Badge
              variant={patent.status === 'Granted' || patent.status === 'granted' ? 'default' : 'secondary'}
              className="text-xs px-2 py-1"
            >
              {patent.status}
            </Badge>
            {patent.patent_type && (
              <Badge variant="outline" className="text-xs px-2 py-1">
                {patent.patent_type}
              </Badge>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 leading-relaxed">
              {patent.title}
            </h3>
            <p className="text-sm text-blue-600 dark:text-blue-400 font-mono bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded inline-block">
              {patent.id}
            </p>
          </div>

          {(() => {
            const chineseAbstract = extractChineseAbstract(patent.abstract)

            if (chineseAbstract) {
              return (
                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3">
                  {chineseAbstract}
                </p>
              )
            } else {
              return (
                <div className="text-sm text-gray-400 dark:text-gray-500 italic text-center py-2">
                  {'查看原始連結中的摘要'}
                </div>
              )
            }
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500 dark:text-gray-400">
            <div className="space-y-2">
              {patent.inventors.length > 0 ? (
                <div className="flex items-start space-x-2">
                  <User className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'發明人'}: </span>
                    <span>{patent.inventors.slice(0, 2).join(', ')}</span>
                    {patent.inventors.length > 2 && <span className="text-gray-400"> 等{patent.inventors.length}人</span>}
                  </div>
                </div>
              ) : (
                <div className="flex items-start space-x-2 text-gray-400">
                  <User className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'發明人'}: </span>
                    <span className="italic">{'點擊連結查看資訊'}</span>
                  </div>
                </div>
              )}
              {patent.assignee && patent.assignee !== '未知' ? (
                <div className="flex items-start space-x-2">
                  <Building className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'受讓人'}: </span>
                    <span>{patent.assignee}</span>
                  </div>
                </div>
              ) : patent.applicants && patent.applicants.length > 0 ? (
                <div className="flex items-start space-x-2">
                  <Building className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'受讓人'}: </span>
                    <span>{patent.applicants.slice(0, 2).join(', ')}</span>
                    {patent.applicants.length > 2 && <span className="text-gray-400"> 等</span>}
                  </div>
                </div>
              ) : (
                <div className="flex items-start space-x-2 text-gray-400">
                  <Building className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'受讓人'}: </span>
                    <span className="italic">{'點擊連結查看資訊'}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              {patent.filingDate && patent.filingDate !== '未知' && (
                <div className="flex items-start space-x-2">
                  <Calendar className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'申請日期'}: </span>
                    <span>{patent.filingDate}</span>
                  </div>
                </div>
              )}
              {patent.publicationDate && patent.publicationDate !== '未知' && (
                <div className="flex items-start space-x-2">
                  <Calendar className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'公開日期'}: </span>
                    <span>{patent.publicationDate}</span>
                  </div>
                </div>
              )}
              {patent.classifications.length > 0 ? (
                <div className="flex items-start space-x-2">
                  <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'分類'}: </span>
                    <span>{patent.classifications.slice(0, 2).join(', ')}</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-start space-x-2 text-gray-400">
                  <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{'分類'}: </span>
                    <span className="italic">{'點擊連結查看資訊'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-1">
                <TrendingUp className="h-4 w-4" />
                <span>{'引用次數'}: {patent.citationCount}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FileText className="h-4 w-4" />
                <span>{'家族大小'}: {patent.familySize}</span>
              </div>
              {patent.language && (
                <div className="flex items-center space-x-1">
                  <span>🌐</span>
                  <span>{'語言'}: {patent.language}</span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {/* 專利詳細資訊連結 - 主要按鈕 */}
              {patent.patent_url && (
                <Button
                  variant="outline"
                  size="default"
                  className="h-10 px-4 text-sm text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/20 flex-shrink-0 font-medium flex items-center"
                  title={'查看詳情'}
                  onClick={() => {
                    logger.info('開啟專利連結:', patent.patent_url)
                    window.open(patent.patent_url, '_blank')
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>{'查看詳情'}</span>
                </Button>
              )}

              {/* 其他功能按鈕 */}
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 flex-shrink-0 flex items-center justify-center"
                title={'複製專利號'}
                onClick={() => navigator.clipboard.writeText(patent.id)}
              >
                <Copy className="h-4 w-4 flex-shrink-0" />
              </Button>

              {/* 偵錯：顯示是否有連結 */}
              {!patent.patent_url && (
                <span className="text-sm text-gray-400" title={'無可用連結'}>
                  <Info className="h-4 w-4" />
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="space-y-8">
      {/* 頁面標題 */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{'專利檢索'}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">{'使用 AI 驅動的專利搜尋引擎'}</p>
        </div>
      </motion.div>

      {/* 搜尋區域 */}
      <Card>
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5" />
            <span>{'專利檢索'}</span>
          </CardTitle>
          <CardDescription>
            {'使用自然語言或關鍵字搜尋全球專利資料庫'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* 關鍵字檢索 */}
          <div className="space-y-4">
            <div>
              <label htmlFor="keyword-search" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                {'關鍵字搜尋'}
              </label>
              <Input
                id="keyword-search"
                placeholder={'輸入關鍵字、專利號或技術描述...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {'支援布林運算符 AND、OR、NOT'}
              </p>
            </div>
          </div>

          {/* 搜尋按鈕區域 */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  onClick={handleSearch}
                  disabled={
                    isSearching ||
                    !searchQuery.trim()
                  }
                  className="flex items-center space-x-2 px-6 py-2.5"
                  size="lg"
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{'搜尋中...'}</span>
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      <span>{'搜尋'}</span>
                    </>
                  )}
                </Button>
              </div>

              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                <Info className="h-4 w-4" />
                <span>
                  {'資料庫資訊'}
                </span>
              </div>
            </div>

            {/* 資料來源說明 - 獨立區塊 */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="flex items-start space-x-2">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-2">{'資料來源:'}</p>
                  <div className="space-y-1">
                    <p>• {'Google Patents Public Datasets API'}</p>
                    <p>• {'支援國際專利資料庫 (USPTO, EPO, JPO, etc.)'}</p>
                    <p>• {'提供雙語支援 (中英文)'}</p>
                    <p>• 官方資料來源</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 搜尋進度 */}
          <AnimatePresence>
            {isSearching && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-3 mt-6"
              >
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">搜尋進度</span>
                  <span className="text-gray-600 dark:text-gray-300">{searchProgress}%</span>
                </div>
                <Progress value={searchProgress} className="h-2" />
                <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                  <Database className="h-3 w-3" />
                  <span>正在搜尋資料庫</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* 搜尋結果 */}
      <AnimatePresence>
        {searchResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* 結果統計 */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="h-5 w-5 text-green-500" />
                      <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">找到 {searchResults.length} 項專利</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      已選擇 {selectedPatents.length} 項
                    </span>
                    {selectedPatents.length > 0 && (
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        匯出選中項目
                      </Button>
                    )}
                  </div>
                </div>

                {/* 新增：顯示各國專利統計 */}
                {searchResults.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center space-x-2 mb-2">
                      <Database className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">專利分佈</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const jurisdictionCounts = searchResults.reduce((acc, patent) => {
                          const jurisdiction = patent.jurisdiction || 'Unknown'
                          acc[jurisdiction] = (acc[jurisdiction] || 0) + 1
                          return acc
                        }, {})

                        const jurisdictionMap = {
                          'US': { country: '🇺🇸', name: '美國' },
                          'EP': { country: '🇪🇺', name: '歐洲' },
                          'JP': { country: '🇯🇵', name: '日本' },
                          'CN': { country: '🇨🇳', name: '中國' },
                          'TW': { country: '🇹🇼', name: '台灣' },
                          'KR': { country: '🇰🇷', name: '韓國' }
                        }

                        return Object.entries(jurisdictionCounts).map(([jurisdiction, count], index) => {
                          const info = jurisdictionMap[jurisdiction] || { country: '🌍', name: jurisdiction }
                          return (
                            <Badge key={`jurisdiction-${jurisdiction}-${index}`} variant="secondary" className="flex items-center space-x-1">
                              <span>{info.country}</span>
                              <span>{info.name}</span>
                              <span className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">{count}</span>
                            </Badge>
                          )
                        })
                      })()}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 結果列表 */}
            <div className="space-y-6">
              {searchResults.map((patent, index) => (
                <PatentCard key={`patent-card-${patent.id}-${index}`} patent={patent} />
              ))}
            </div>

            {/* 載入更多 */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="flex items-center space-x-2"
              >
                {isLoadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    <span>載入更多中</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>載入更多結果</span>
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default PatentSearch

